"use client";

import Image from "next/image";
import Link from "next/link";
import { Eye, ShoppingCart, Share2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useCart } from "@/features/cart/hook/useCart";
import { Product } from "@/types/product.types";
import { toast } from "sonner";

interface Props {
    product: Product;
    onQuickView?: ((product: Product) => void) | undefined;
}

export const ProductListCard = ({ product, onQuickView }: Props) => {
    const { addItem } = useCart();



    const weightAttribute = product?.attributes?.find(
        (attr) => (attr?.attribute?.options ?? []).length > 0
    );

    const weightOptions = weightAttribute?.attribute?.options ?? [];

    const [selectedWeight, setSelectedWeight] = useState<string>(
        String(weightOptions?.[0]?.value ?? "")
    );

    const handleAddToCart = async () => {
        const option =
            weightOptions.find((o) => String(o.value) === selectedWeight) ||
            weightOptions[0];

        await addItem(product.id, option?.id || '', 1);
    };

    const handleShare = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (typeof window === "undefined") return;
        const url = `${window.location.origin}/products/${product.slug}`;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: product.name,
                    text: product.description || undefined,
                    url: url,
                });
            } catch (err) {
                console.error("Error sharing:", err);
            }
        } else {
            try {
                await navigator.clipboard.writeText(url);
                toast.success("Lien copié dans le presse-papiers !");
            } catch (err) {
                console.error("Error copying to clipboard:", err);
                toast.error("Erreur lors de la copie du lien");
            }
        }
    };

    return (
        <div className="flex w-full items-center gap-10 rounded-3xl bg-white border border-primary-200 p-6">
            {/* IMAGE */}
            <div
                className="relative h-40 w-40 shrink-0 rounded-2xl bg-gray-50 overflow-hidden cursor-pointer"
                onClick={() => onQuickView?.(product)}
            >
                <Image
                    src={product.images?.[0]?.url ?? "/placeholder.png"}
                    alt={product.name}
                    fill
                    sizes="160px"
                    className="object-contain"
                />
            </div>

            {/* CONTENT */}
            <div className="flex flex-1 items-center justify-between gap-10">
                {/* LEFT */}
                <div className="max-w-xl space-y-2">
                    <p className="text-sm text-gray-400">
                        Product cosmétique bio (Categorie)
                    </p>

                    <Link
                        href={`/products/${product.slug}`}
                        className="block hover:text-[#8B6F47] transition"
                    >
                        <h3 className="text-xl font-medium text-primary-900">
                            {product.name}
                        </h3>
                        {product.subName && (
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                {product.subName}
                            </p>
                        )}
                    </Link>

                    <p className="text-sm text-gray-500 line-clamp-2">
                        {product.description}
                    </p>
                </div>

                {/* RIGHT */}
                <div className="flex flex-col items-end gap-4">
                    <div className="flex items-center gap-6">
                        {/* Weight selector */}
                        {weightOptions.length > 0 && (
                            <Select
                                value={selectedWeight}
                                onValueChange={(value) => setSelectedWeight(value)}
                            >
                                <SelectTrigger className="w-28 h-10 rounded-xl border border-gray-200 font-medium text-primary-700">
                                    <SelectValue placeholder="Poids" />
                                </SelectTrigger>

                                <SelectContent align="end">
                                    {weightOptions.map((option) => (
                                        <SelectItem
                                            key={option.id}
                                            value={String(option.value)}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        <Link
                            href={`/products/${product.slug}`}
                            className="text-2xl font-semibold text-primary-900 hover:text-[#8B6F47] transition whitespace-nowrap"
                        >
                            {Number(product.price).toLocaleString()} DA
                        </Link>
                    </div>

                    <Link href={`/products/${product.slug}`}>
                        <Button className="rounded-lg px-6">
                            Acheter maintenant
                        </Button>
                    </Link>

                    <div className="flex gap-8">
                        <Button size="icon" onClick={handleAddToCart}>
                            <ShoppingCart className="h-5 w-5" />
                        </Button>

                        <Button size="icon" onClick={() => onQuickView?.(product)}>
                            <Eye className="h-5 w-5" />
                        </Button>

                        <Button size="icon" onClick={handleShare}>
                            <Share2 className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};